import { useEffect, useRef, useState } from "react";
import { Camera, Plus, Trash2, Save, ImagePlus, Sparkles, Info, Wand2, RotateCcw, Loader2 } from "lucide-react";
import DashboardShell from "@/components/hola/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const MAX_ITEMS = 10;
const MAX_BYTES = 700_000; // 700KB post-compress

/**
 * Comprime una imagen a JPEG max 900px lado largo, calidad 82.
 * Devuelve un data URL o rechaza si excede MAX_BYTES.
 */
async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("No pude leer el archivo"));
    reader.onload = () => {
      const img = new Image();
      img.onerror = () => reject(new Error("Archivo no es una imagen válida"));
      img.onload = () => {
        const MAX_DIM = 900;
        let { width, height } = img;
        if (width > height && width > MAX_DIM) {
          height = Math.round((height * MAX_DIM) / width);
          width = MAX_DIM;
        } else if (height > MAX_DIM) {
          width = Math.round((width * MAX_DIM) / height);
          height = MAX_DIM;
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        // Bajar calidad progresiva si el resultado excede MAX_BYTES
        let quality = 0.82;
        let dataUrl = canvas.toDataURL("image/jpeg", quality);
        while (dataUrl.length > MAX_BYTES && quality > 0.4) {
          quality -= 0.1;
          dataUrl = canvas.toDataURL("image/jpeg", quality);
        }
        if (dataUrl.length > MAX_BYTES) {
          return reject(new Error("La imagen es muy pesada aún comprimida. Usa una foto más pequeña."));
        }
        resolve(dataUrl);
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

export default function CatalogPage() {
  return (
    <DashboardShell active="catalog">
      {() => <CatalogBody />}
    </DashboardShell>
  );
}

function CatalogBody() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [quota, setQuota] = useState(null); // {used_this_month, limit, remaining}
  const [designingId, setDesigningId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [catRes, quotaRes] = await Promise.all([
          api.get("/merchants/me/catalog"),
          api.get("/merchants/me/design/quota").catch(() => ({ data: null })),
        ]);
        setItems(catRes.data.items || []);
        if (quotaRes?.data) setQuota(quotaRes.data);
      } catch (e) {
        toast.error("No se pudo cargar el catálogo");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const addItem = () => {
    if (items.length >= MAX_ITEMS) {
      toast.info(`Máximo ${MAX_ITEMS} productos`);
      return;
    }
    setItems((arr) => [
      ...arr,
      { id: `ci_new_${Date.now()}`, image: "", title: "", description: "", price: "" },
    ]);
  };

  const updateItem = (idx, patch) => {
    setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };

  const removeItem = (idx) => {
    setItems((arr) => arr.filter((_, i) => i !== idx));
  };

  const designWithAI = async (idx) => {
    const item = items[idx];
    if (!item?.id || item.id.startsWith("ci_new_")) {
      toast.error("Guarda el producto primero (título + foto) antes de diseñarlo con IA");
      return;
    }
    if (!item?.image) {
      toast.error("Sube una foto original antes de aplicar el diseño IA");
      return;
    }
    if (quota && quota.remaining <= 0) {
      toast.error(`Ya usaste tus ${quota.limit} diseños este mes`);
      return;
    }
    setDesigningId(item.id);
    try {
      const { data } = await api.post("/merchants/me/catalog/design", {
        item_id: item.id,
        style: "elegante",
      });
      updateItem(idx, { image_ai: data.image_ai, use_ai: true, ai_designed_at: new Date().toISOString() });
      setQuota((q) => q ? { ...q, used_this_month: data.used_this_month, remaining: data.remaining } : q);
      toast.success("¡Diseño IA aplicado!");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.error === "design_quota_exhausted") {
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === "string" ? detail : "No se pudo generar el diseño");
      }
    } finally {
      setDesigningId(null);
    }
  };

  const toggleAI = async (idx) => {
    const item = items[idx];
    if (!item?.image_ai) return;
    const next = !item.use_ai;
    // Optimista
    updateItem(idx, { use_ai: next });
    try {
      await api.post("/merchants/me/catalog/design/toggle", {
        item_id: item.id,
        use_ai: next,
      });
    } catch (e) {
      updateItem(idx, { use_ai: !next }); // rollback
      toast.error("No se pudo cambiar la versión");
    }
  };

  const save = async () => {
    // Validación rápida: al menos título
    for (let i = 0; i < items.length; i++) {
      if (!items[i].title?.trim()) {
        toast.error(`Producto #${i + 1}: falta el título`);
        return;
      }
    }
    setSaving(true);
    try {
      const { data } = await api.put("/merchants/me/catalog", { items });
      setItems(data.items || []);
      toast.success(`Catálogo guardado (${data.items.length} productos)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-neutral-500">Cargando catálogo…</div>;
  }

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <p className="text-xs uppercase tracking-widest text-neutral-500 font-bold">Catálogo visual</p>
        <h1 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">
          10 productos que Aura mostrará a los clientes
        </h1>
        <p className="text-sm text-neutral-500 mt-2 leading-relaxed max-w-2xl">
          Sube hasta <strong>10 fotos</strong> de tus productos o servicios estrella. Cuando un usuario le pregunte a Aura por
          algo del catálogo, ella podrá mostrarle la foto y el precio en el chat — pero siempre lo invitará a visitarte en persona.
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-[#FF007A]/10 to-[#0055FF]/10 border border-[#FF007A]/20 p-4 flex gap-3">
        <div className="w-9 h-9 rounded-full bg-[#FF007A] text-white grid place-items-center shrink-0">
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="text-sm text-neutral-700 leading-relaxed flex-1">
          <strong>Tip:</strong> muestra <strong>lo más vendible</strong> y lo que mejor se vea en foto. Aura NO cierra la venta por
          chat — su misión es que el cliente camine los pasos que le faltan hasta tu tienda física.
        </div>
        {quota && (
          <div className="text-right shrink-0" data-testid="catalog-ai-quota">
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-bold">Diseños IA</div>
            <div className="font-heading font-extrabold text-lg text-[#FF007A]">
              {quota.remaining} <span className="text-xs text-neutral-500 font-normal">/ {quota.limit}</span>
            </div>
            <div className="text-[10px] text-neutral-500">restantes este mes</div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4" data-testid="catalog-items-grid">
        {items.map((it, idx) => (
          <CatalogItemCard
            key={it.id || idx}
            index={idx}
            item={it}
            onChange={(patch) => updateItem(idx, patch)}
            onRemove={() => removeItem(idx)}
            onDesignAI={() => designWithAI(idx)}
            onToggleAI={() => toggleAI(idx)}
            designing={designingId === it.id}
            quotaRemaining={quota?.remaining ?? null}
          />
        ))}

        {items.length < MAX_ITEMS && (
          <button
            type="button"
            onClick={addItem}
            className="rounded-2xl border-2 border-dashed border-neutral-300 hover:border-[#FF007A] hover:bg-[#FF007A]/5 transition-colors p-8 flex flex-col items-center justify-center gap-2 text-neutral-500 hover:text-[#FF007A] min-h-[220px]"
            data-testid="catalog-add-btn"
          >
            <div className="w-11 h-11 rounded-full bg-white grid place-items-center border border-current">
              <Plus className="w-5 h-5" />
            </div>
            <div className="text-sm font-bold">Agregar producto</div>
            <div className="text-xs">{items.length} / {MAX_ITEMS}</div>
          </button>
        )}
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-200 p-3 flex gap-2 text-xs text-blue-900">
        <Info className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          Las fotos se comprimen automáticamente antes de subirlas (máx 700KB · JPEG). Los productos sin imagen también son válidos —
          Aura mencionará su nombre y precio, pero no mostrará foto.
        </div>
      </div>

      <div className="sticky bottom-4 md:static z-20 flex justify-end">
        <Button
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[#0055FF] hover:bg-[#0044cc] font-bold h-12 px-8 shadow-lg"
          data-testid="catalog-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Guardando…" : `Guardar (${items.length})`}
        </Button>
      </div>
    </div>
  );
}

function CatalogItemCard({ index, item, onChange, onRemove, onDesignAI, onToggleAI, designing, quotaRemaining }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // reset input
    if (!f) return;
    setUploading(true);
    try {
      const dataUrl = await compressImage(f);
      // Al subir NUEVA foto, resetear la versión AI (ya no aplica al producto nuevo)
      onChange({ image: dataUrl, image_ai: "", use_ai: false });
    } catch (err) {
      toast.error(err.message || "No pude subir la imagen");
    } finally {
      setUploading(false);
    }
  };

  const displayImage = item.use_ai && item.image_ai ? item.image_ai : item.image;
  const hasAI = Boolean(item.image_ai);
  const isNew = (item.id || "").startsWith("ci_new_");
  const canDesign = !isNew && Boolean(item.image) && !designing && (quotaRemaining === null || quotaRemaining > 0);

  return (
    <div className="rounded-2xl bg-white border border-neutral-200 overflow-hidden" data-testid={`catalog-item-${index}`}>
      <div className="relative aspect-video bg-neutral-100 flex items-center justify-center">
        {displayImage ? (
          <img src={displayImage} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="flex flex-col items-center gap-2 text-neutral-400">
            <Camera className="w-8 h-8" />
            <div className="text-xs">Sin foto</div>
          </div>
        )}
        <div className="absolute top-2 left-2 flex gap-1">
          <div className="bg-black/70 text-white text-[10px] font-bold px-2 py-1 rounded-full">
            #{index + 1}
          </div>
          {hasAI && (
            <div
              className={`text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 ${
                item.use_ai ? "bg-[#FF007A] text-white" : "bg-white/90 text-neutral-700"
              }`}
              data-testid={`catalog-ai-badge-${index}`}
            >
              <Sparkles className="w-3 h-3" /> {item.use_ai ? "IA" : "Original"}
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading || designing}
            className="bg-white/90 hover:bg-white text-neutral-800 text-xs font-bold px-3 py-1.5 rounded-full flex items-center gap-1 shadow"
            data-testid={`catalog-upload-${index}`}
          >
            <ImagePlus className="w-3.5 h-3.5" />
            {uploading ? "Subiendo…" : item.image ? "Cambiar" : "Subir foto"}
          </button>
          <button
            type="button"
            onClick={onRemove}
            disabled={designing}
            className="bg-red-500/90 hover:bg-red-600 disabled:opacity-50 text-white p-1.5 rounded-full shadow"
            data-testid={`catalog-remove-${index}`}
            aria-label="Eliminar producto"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden" />

        {/* AI design overlay while generating */}
        {designing && (
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm grid place-items-center text-white gap-2 z-10">
            <Loader2 className="w-6 h-6 animate-spin" />
            <div className="text-xs font-bold">Nano Banana está diseñando…</div>
            <div className="text-[10px] text-neutral-300">Esto toma ~10-20s</div>
          </div>
        )}
      </div>

      {/* AI design action bar */}
      <div className="px-4 pt-3 pb-1 flex items-center gap-2">
        {!hasAI ? (
          <Button
            type="button"
            onClick={onDesignAI}
            disabled={!canDesign}
            variant="outline"
            className="flex-1 h-9 rounded-full border-[#FF007A]/40 text-[#FF007A] hover:bg-[#FF007A]/5 font-bold text-xs disabled:opacity-40"
            data-testid={`catalog-design-ai-${index}`}
            title={isNew ? "Guarda primero" : !item.image ? "Sube una foto primero" : "Genera un fondo de marca con IA"}
          >
            <Wand2 className="w-3.5 h-3.5" /> Diseñar con IA
          </Button>
        ) : (
          <>
            <Button
              type="button"
              onClick={onToggleAI}
              variant="outline"
              className="flex-1 h-9 rounded-full font-bold text-xs"
              data-testid={`catalog-toggle-ai-${index}`}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              {item.use_ai ? "Ver original" : "Ver diseño IA"}
            </Button>
            <Button
              type="button"
              onClick={onDesignAI}
              disabled={!canDesign}
              variant="outline"
              className="h-9 rounded-full border-[#FF007A]/40 text-[#FF007A] hover:bg-[#FF007A]/5 font-bold text-xs disabled:opacity-40 px-3"
              data-testid={`catalog-redesign-ai-${index}`}
              title="Regenerar diseño (consume 1 cuota)"
            >
              <Wand2 className="w-3.5 h-3.5" />
            </Button>
          </>
        )}
      </div>

      <div className="p-4 space-y-3">
        <div>
          <Label className="text-xs">Nombre del producto</Label>
          <Input
            value={item.title || ""}
            onChange={(e) => onChange({ title: e.target.value })}
            placeholder="Café latte, Blusa de lino, iPhone 15…"
            className="mt-1 h-10 rounded-xl"
            data-testid={`catalog-title-${index}`}
            maxLength={120}
          />
        </div>
        <div>
          <Label className="text-xs">Precio</Label>
          <Input
            value={item.price || ""}
            onChange={(e) => onChange({ price: e.target.value })}
            placeholder="$65 MXN · desde $120 · $1,500"
            className="mt-1 h-10 rounded-xl"
            data-testid={`catalog-price-${index}`}
            maxLength={60}
          />
        </div>
        <div>
          <Label className="text-xs">Descripción corta <span className="text-neutral-400">(opcional)</span></Label>
          <Textarea
            value={item.description || ""}
            onChange={(e) => onChange({ description: e.target.value })}
            placeholder="Con leche vaporizada, corte oversize, 128GB..."
            rows={2}
            className="mt-1 rounded-xl text-sm"
            data-testid={`catalog-desc-${index}`}
            maxLength={400}
          />
        </div>
      </div>
    </div>
  );
}
